import { useCallback, useMemo } from 'react';
import { useMutation } from '@apollo/react-hooks';
import { useProject } from '@atlassian/jira-business-entity-project-hook/src/index.tsx';
import {
	useExperienceStart,
	useExperienceSuccess,
	useExperienceFail,
} from '@atlassian/jira-business-experience-tracking/src/controllers/experience-tracker/index.tsx';
import type { ExperienceDetails } from '@atlassian/jira-business-experience-tracking/src/types.tsx';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { useFlagService } from '@atlassian/jira-flags/src/services'; // ignore-for-ENGHEALTH-17759
import {
	fireOperationalAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import type {
	JwmUpdateCategory,
	JwmUpdateCategoryVariables,
	JwmUpdateCategory_updateJwmCategory_category as JwmUpdatedCategory,
} from './__generated_apollo__/JwmUpdateCategory';
import { QUERY_NAME, UPDATE_FAILED_FLAG, UPDATE_SUCCESS_FLAG } from './constants.tsx';
import { UPDATE_CATEGORY } from './gql.tsx';
import type { UpdateCategoryProps } from './types.tsx';

export const useUpdateCategory = ({
	experience,
	onCompleted,
}: {
	experience: ExperienceDetails;
	onCompleted?: (data: JwmUpdateCategory) => void;
}) => {
	const startExperience = useExperienceStart(experience);
	const markExperienceSuccess = useExperienceSuccess(experience);
	const markExperienceFailed = useExperienceFail(experience);

	const { showFlag } = useFlagService();
	const project = useProject();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const projectId = String(project.id);

	const [updateCategory, { data, loading, error }] = useMutation<
		JwmUpdateCategory,
		JwmUpdateCategoryVariables
	>(UPDATE_CATEGORY, {
		onError: (apolloError) => {
			markExperienceFailed(`${experience.experience} category service error handling`, apolloError);
			showFlag(UPDATE_FAILED_FLAG);

			fireErrorAnalytics({
				meta: {
					id: 'jwmUpdateCategory',
					packageName: 'jiraBusinessCategories',
					teamName: 'jira-warepil',
				},
				attributes: {
					message: 'Failed to update category',
				},
				error: apolloError,
				sendToPrivacyUnsafeSplunk: true,
			});
		},
		onCompleted: (onCompletedData: JwmUpdateCategory) => {
			if (onCompletedData?.updateJwmCategory?.errors) {
				showFlag(UPDATE_FAILED_FLAG);
				return;
			}

			markExperienceSuccess();
			showFlag(UPDATE_SUCCESS_FLAG);
			fireOperationalAnalytics(createAnalyticsEvent({}), `${QUERY_NAME} succeeded`);
			if (onCompleted) {
				onCompleted(onCompletedData);
			}
		},
	});

	const update = useCallback(
		async ({
			issueTypeId,
			optionId,
			value,
			color,
		}: UpdateCategoryProps): Promise<JwmUpdatedCategory | null | undefined> => {
			startExperience();

			const response = await updateCategory({
				variables: {
					input: {
						projectId,
						issueTypeId,
						optionId,
						value,
						color,
					},
				},
			});

			const result = response?.data?.updateJwmCategory;
			if (result?.success) {
				if (result.category) {
					return result.category;
				}
			}
			return null;
		},
		[startExperience, updateCategory, projectId],
	);

	// find error that has message
	const mutationBadRequestError = useMemo(
		() => data?.updateJwmCategory?.errors?.find((e) => e.message),
		[data],
	);

	return {
		error: error || mutationBadRequestError,
		data,
		loading,
		updateCategory: update,
	};
};
